import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { X as XIcon } from 'lucide-react';

interface ImageThumbnailProps {
	assetId?: string;
	src?: string;
	alt?: string;
	/** Fixed height in px — width adjusts to preserve original aspect ratio */
	height?: number;
	className?: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Cache presigned URLs by asset ID
const urlCache = new Map<string, { url: string; fetchedAt: number }>();
const CACHE_TTL = 50 * 60 * 1000; // 50 minutes

async function fetchAssetUrl(assetId: string): Promise<string> {
	const cached = urlCache.get(assetId);
	if (cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
		return cached.url;
	}

	const { data: { session } } = await supabase.auth.getSession();
	if (!session?.access_token) {
		console.warn('[ImageThumbnail] No auth session');
		return '';
	}

	try {
		const res = await fetch(`${API_URL}/assets/${assetId}`, {
			headers: { Authorization: `Bearer ${session.access_token}` },
		});
		if (!res.ok) {
			console.warn('[ImageThumbnail] Failed to fetch asset:', assetId, res.status);
			return '';
		}
		const data = await res.json();
		const url = data.url || '';
		if (url) {
			urlCache.set(assetId, { url, fetchedAt: Date.now() });
		}
		return url;
	} catch (err) {
		console.warn('[ImageThumbnail] Fetch error for asset:', assetId, err);
		return '';
	}
}

const ImageThumbnail: React.FC<ImageThumbnailProps> = ({
	assetId,
	src,
	alt = 'Image',
	height = 64,
	className = '',
}) => {
	const [open, setOpen] = useState(false);
	const [broken, setBroken] = useState(false);
	const [resolvedUrl, setResolvedUrl] = useState<string>(src || '');
	const [loading, setLoading] = useState(!src && !!assetId);

	useEffect(() => {
		if (src) {
			setResolvedUrl(src);
			setLoading(false);
			return;
		}
		if (!assetId) {
			setResolvedUrl('');
			setLoading(false);
			return;
		}

		let cancelled = false;
		setLoading(true);
		setBroken(false);

		fetchAssetUrl(assetId).then((url) => {
			if (cancelled) return;
			setResolvedUrl(url);
			setLoading(false);
			if (!url) setBroken(true);
		});

		return () => { cancelled = true; };
	}, [assetId, src]);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === 'Escape' && open) setOpen(false);
		},
		[open],
	);

	useEffect(() => {
		if (open) {
			document.addEventListener('keydown', handleKeyDown);
			return () => document.removeEventListener('keydown', handleKeyDown);
		}
	}, [open, handleKeyDown]);

	if (!assetId && !src) return null;

	if (loading) {
		return (
			<div
				className={`animate-pulse rounded bg-zinc-200 dark:bg-zinc-700 ${className}`}
				style={{ height, aspectRatio: '3 / 2' }}
			/>
		);
	}

	if (broken || !resolvedUrl) {
		return (
			<div
				className={`flex items-center justify-center rounded bg-zinc-100 text-xs text-zinc-400 dark:bg-zinc-800 ${className}`}
				style={{ height, aspectRatio: '3 / 2' }}
			>
				?
			</div>
		);
	}

	return (
		<>
			<img
				src={resolvedUrl}
				alt={alt}
				className={`cursor-pointer rounded object-cover ${className}`}
				style={{ height, width: 'auto' }}
				loading="lazy"
				onError={() => setBroken(true)}
				onClick={() => setOpen(true)}
			/>

			{open && (
				<div
					className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
					onClick={() => setOpen(false)}
				>
					<button
						className="absolute right-4 top-4 rounded-full bg-black/50 p-2 text-white hover:bg-black/80"
						onClick={(e) => {
							e.stopPropagation();
							setOpen(false);
						}}
					>
						<XIcon className="h-5 w-5" />
					</button>
					<img
						src={resolvedUrl}
						alt={alt}
						className="max-h-[90vh] max-w-[90vw] rounded object-contain"
						onClick={(e) => e.stopPropagation()}
					/>
				</div>
			)}
		</>
	);
};

export default ImageThumbnail;
